type CloseAccountAvailabilityParams = {
  /**
   * Whether the viewer holds `DESTROY` on this card — the permission
   * `PUT /creditcards/:id/close` itself checks. `undefined` while the
   * permissions query is in flight; a destructive action fails closed, so that
   * reads as "not permitted" until it resolves.
   */
  canDestroyCard: boolean | undefined
  /** `CreditCardListModel.isClosed` — displayStatus === CLOSED. */
  isClosed: boolean
}

/**
 * Whether the account header offers "Close Account".
 *
 * The dropdown this feeds is the only entry point to the close flow —
 * `CloseAccountBox` exists but is rendered nowhere — so anything this withholds
 * is unreachable, not merely inconvenient. That cuts both ways: it must not
 * offer an action the server will reject either.
 *
 * Gated on `DESTROY`, matching the permission `CreditCardsController.close`
 * resolves the card with, and matching how this page already gates its other
 * destructive card actions (`usePhysicalCardLifecycle`'s `canDestroyCard`) and
 * how `DeactivateBudget` gates its own. It previously read
 * `UPDATE_CREDIT_CARD`, which is neither necessary nor sufficient for the close
 * this routes into.
 *
 * Beyond permission, only an already-closed account is excluded. This
 * deliberately does NOT require the card to be active: `displayStatus` has just
 * four values (ACTIVE / INACTIVE / PENDING / CLOSED), so an `isActive` gate
 * stranded every pending account and every expired one (expiry surfaces as
 * INACTIVE — there is no EXPIRED display status), along with any physical card
 * whose transactions feed had been disconnected. The close endpoint imposes no
 * status precondition of its own — it resolves the card and queues the job —
 * and `CloseAccountContent` guards the modal on `isClosed` alone, so this now
 * admits exactly the cards its destination serves.
 *
 * Takes the model's derived `isClosed` rather than importing
 * `CreditCardDisplayStatus`: six suites stub `creditCard.models` partially, and
 * a leaf helper that reaches for the enum breaks under every stub that has not
 * enumerated it. See `creditCard.models.test.ts` for the status → `isClosed`
 * mapping this relies on.
 */
export const canCloseAccount = ({
  canDestroyCard,
  isClosed,
}: CloseAccountAvailabilityParams): boolean =>
  canDestroyCard === true && !isClosed
